import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Notification, NotificationBody, NotificationFooter } from '@adeccoux/tag-ds';
import { Loader } from '@components/shared/Loader/Loader';
import { useInjection } from '@context/inversify-context-provider';
import { OONavigationPages } from '@features/oneOnboarding/interfaces';
import { OOUserDocumentModel } from '@features/oneOnboarding/models/UserDocumentModel';
import { getStepperComponent } from '@helpers/getStepperComponent';
import { SignDocumentsStatus } from '@helpers/OnboardingSteps';
import {
  mutateAcknowledgeDocument,
  mutateDocumentViewStatus,
  useAllApplicationsForUserAndClientOrgUploadLimitSize,
  useCheckGeneratedDocumentsStatus,
} from '@hooks/apiHooks';
import useTelemetry from '@hooks/useTelemetry';
import { TYPES } from '@libs/InversifyType';
import { EventType, TargetName, TelemetryActor } from '@models/telemetry.model';
import { Box, Button } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import BottomBox from '../../../../components/shared/BottomBox/BottomBox';
import { AamBackendApi } from '../../../../libs/aamBackendApi';
import { CandidatePageLayout } from '../../components/CandidatePageLayout/CandidatePageLayout';
import { OONavigationContext } from '../../context/NavigationContext';
import { useFlowWrapper } from '../../hooks/useFlowWrapper';
import { DocumentsForSignatureModel, OOStepModel } from '../../models/StepModel';

import { OOSignDocumentStart } from './SignatureStart';
import { SignDocumentButton } from './SignDocumentButton';
import { SignDocumentPreview } from './SignDocumentPreview';

import styles from './SignDocuments.module.scss';
const dictionaries = ['documents', 'hiring', 'approval'];
const POLLING_INTERVAL_MS = 10 * 1000;
const MAX_ATTEMPTS = 5;
const isCompleted = (flow: any[], step: string, currentStep: string) => {
  const stepIndex = flow.findIndex((s) => s.name === step);
  const currentStepIndex = flow.findIndex((s) => s.name === currentStep);
  return stepIndex < currentStepIndex;
};

export const OOCandidateSignDocuments: React.FC<any> = () => {
  const [selectedDoc, setSelectedDoc] = useState<null | any>(null);
  const [hasIntro, setHasIntro] = useState(false);
  const [isLoadingStepper, setIsLoadingStepper] = useState(true);

  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const location = useLocation();
  const { t, i18n } = useTranslation([...dictionaries, 'stepper']);
  const navigationContext = React.useContext(OONavigationContext);
  const { userId } = useSelector((state: RootState) => state.authApp);
  const { data = [], isFetching } = useAllApplicationsForUserAndClientOrgUploadLimitSize(userId);
  const queryClient = useQueryClient();
  const { clientOrganizationId, selectedConfigurationId, step, flow, currentStep, applicationId } = useSelector(
    (state: RootState) => state.oneOnboarding,
  );
  const stepsOverviewHidden = useSelector((state: RootState) => state.oneOnboarding.stepsOverviewHidden);
  const { themeAdditionalData } = useSelector((state: RootState) => state.oneOnboarding);
  const query = new URLSearchParams(location.search);
  const signed = query.get('signed');
  const redirectToNextStep = query.get('redirectToNextStep');
  const history = useHistory();
  const { createTelemetryAuthenticated } = useTelemetry();
  const tenantId = localStorage.getItem('tenantId');
  const [isDocumentViewed, setIsDocumentViewed] = useState(false);
  const [isDocumentsMissing, setIsDocumentsMissing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasGeneratedDocument, setHasGeneratedDocument] = useState(false);
  const [documentsForSignature, setDocumentsForSignature] = useState<DocumentsForSignatureModel[]>([]);
  useEffect(() => {
    createTelemetryAuthenticated({
      applicationId: applicationId,
      clientOrganizationId: clientOrganizationId,
      configurationId: selectedConfigurationId,
      eventType: EventType.PAGE_LOADED,
      tenantId: tenantId as any,
      targetName: TargetName.DocumentsToSign,
      actor: TelemetryActor.CANDIDATE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoadingStepper(true);
    const overviewFromHistory = history.location.state?.overview;

    if (overviewFromHistory === false || stepsOverviewHidden) {
      setHasIntro(false);
    } else {
      setHasIntro(!isCompleted(flow, step, currentStep));
    }

    setIsLoadingStepper(false);
  }, [history, currentStep, flow, step, stepsOverviewHidden]);

  const { mutate: signDocument, isPending, isSuccess, isError } = useMutation({
    mutationFn: (document: any) =>
      aamBackendApi.getOneSpanOOSignAllDocumentUrl(clientOrganizationId || '', {
        document: document.name,
        callbackUrl: `${window.location.origin}/oo/onespan-callback`,
        returnUrl: window.location.href,
        clientOrganizationId,
        configurationId: selectedConfigurationId,
        applicationId: applicationId || localStorage.getItem('applicationId'),
        additionalReturnParams: {
          documentId: document.id,
          lang: i18n.language,
          signerId: userId,
        },
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const {
    mutate: completeStepForUser,
    isPending: isCompleting,
    isSuccess: isCompletingSuccess,
    isError: isCompletingFailed,
  } = useMutation({
    mutationFn: () =>
      aamBackendApi.ooCompleteDocumentForSignatureStep(
        clientOrganizationId as any,
        selectedConfigurationId as any,
        userId as any,
        {
          stepName: step,
        },
      ),
    onSuccess: (response) => {
      if (response.currentStep === 'FEEDBACK_STEP') {
        const feedbackStep = response.selectedConfiguration?.flow?.find(
          (step: OOStepModel) => step.name === 'FEEDBACK_STEP',
        );
        navigationContext.to(`FEEDBACK_STEP_${feedbackStep?.pages?.[0]?.name}` as OONavigationPages);
        return;
      }
      navigationContext.forward();
    },
  });

  useEffect(() => {
    if (redirectToNextStep === 'true' && clientOrganizationId && step) {
      history.push({
        pathname: history.location.pathname,
        search: '',
      });
      location.search = '';
      completeStepForUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeStepForUser, history, redirectToNextStep, clientOrganizationId]);

  const flowWrapper = useFlowWrapper();
  useEffect(() => {
    const stepData = flowWrapper?.getStep(step);
    setDocumentsForSignature(stepData?.documentsForSignature || []);
    if (stepData?.documentsForSignature) {
      const journeyHasGeneratedDocuments = stepData.documentsForSignature.some(
        (doc) => doc.candidateAcknowledgementRequired || doc.generated,
      );
      setHasGeneratedDocument(journeyHasGeneratedDocuments);
    }
  }, [flowWrapper, step]);
  const shouldPollForMissingDocuments = isDocumentsMissing && retryCount < MAX_ATTEMPTS;
  const { isLoading: isAwaitingDocumentStatus, refetch: refetchDocumentsManually } = useCheckGeneratedDocumentsStatus(
    tenantId!,
    clientOrganizationId!,
    selectedConfigurationId!,
    userId!,
    applicationId!,
    retryCount,
    hasGeneratedDocument,
    (data: { missingDocuments: string[]; unAcknowledgedDocuments: Record<string, OOUserDocumentModel> }) => {
      const { unAcknowledgedDocuments, missingDocuments } = data;
      if (missingDocuments.length > 0) {
        setIsDocumentsMissing(true);
        setRetryCount((prev) => prev + 1);
      } else {
        setIsDocumentsMissing(false);
        setRetryCount(MAX_ATTEMPTS);
      }
      if (Object.keys(unAcknowledgedDocuments).length === 0 || missingDocuments.length > 0) return;
      setDocumentsForSignature((prev) =>
        prev.map((doc) => {
          const updatedDoc = unAcknowledgedDocuments[doc.name];
          if (!updatedDoc) return doc;
          const existingDocs = doc.userDocuments ?? [];
          const index = existingDocs.findIndex((d) => d.id === updatedDoc.id);
          const updatedUserDocuments =
            index !== -1
              ? existingDocs.map((doc, i) => (i === index ? updatedDoc : doc))
              : [...existingDocs, updatedDoc];
          return {
            ...doc,
            userDocuments: updatedUserDocuments,
          };
        }),
      );
    },
    shouldPollForMissingDocuments ? POLLING_INTERVAL_MS : false,
  );

  const stepLabelKey: string = useMemo(() => {
    if (!flowWrapper) {
      return 'candidate_recruiter:APPROVAL.SIGNATURE.signDocuments';
    }
    const stepData: any = flowWrapper.getStep(step);
    if (!stepData) {
      return 'candidate_recruiter:APPROVAL.SIGNATURE.signDocuments';
    }
    return `stepper:${stepData.label}`;
  }, [flowWrapper, step]);

  const currentPageAndStep = useMemo(() => {
    return flowWrapper?.getCurrentStepAndFormFromNavigationPage(navigationContext.currentPage);
  }, [navigationContext, flowWrapper]);

  const areAllDocsSignedByCandidate = useMemo(() => {
    return documentsForSignature
      .filter((d: any) => d.isCandidateSignatureRequired)
      .map((d: any) => d?.userDocuments?.find((doc: any) => doc.additionalData.signed === true))
      .every((d: any) => d?.additionalData?.signed === true);
  }, [documentsForSignature]);

  const areAllDocsAcknowledgedByCandidate = useMemo(() => {
    const latestAcknowledgedDocs = documentsForSignature
      .filter((doc) => doc.candidateAcknowledgementRequired)
      .map((doc) => {
        const latestUserDoc = doc.userDocuments?.reduce((latest, current) => {
          const latestTime = new Date(latest.createdAt || 0).getTime();
          const currentTime = new Date(current.createdAt || 0).getTime();
          return currentTime > latestTime ? current : latest;
        }, doc.userDocuments?.[0]);

        return latestUserDoc?.additionalData?.acknowledged === true;
      });

    return latestAcknowledgedDocs.every(Boolean);
  }, [documentsForSignature]);

  const handleRefetchDocuments = () => {
    refetchDocumentsManually();
  };

  const updateDocumentInformation = (document: OOUserDocumentModel, updatedFields: Partial<OOUserDocumentModel>) => {
    setDocumentsForSignature((prev) =>
      prev.map((doc) => {
        if (doc.name !== document.type) return doc;

        const userDocuments = doc.userDocuments.map((userDoc) =>
          userDoc.id === document.id ? { ...userDoc, ...updatedFields } : userDoc,
        );
        return { ...doc, userDocuments };
      }),
    );
  };

  const handleDocumentViewed = (data: OOUserDocumentModel) => {
    if (data?.additionalData?.seen) {
      setIsDocumentViewed(true);
      const document = data;
      updateDocumentInformation(document, { additionalData: { ...document.additionalData } });
    }
  };

  const { mutate: updateDocumentViewStatus } = mutateDocumentViewStatus(tenantId!, userId!, handleDocumentViewed);

  const handleAcknowledgement = (data: { currentDocument: OOUserDocumentModel; nextDocument: OOUserDocumentModel }) => {
    if (data?.currentDocument?.additionalData?.acknowledged) {
      updateDocumentInformation(data?.currentDocument, {
        additionalData: { ...data?.currentDocument.additionalData },
      });
      if (data?.nextDocument) {
        const doc = documentsForSignature.find((doc) =>
          doc?.userDocuments?.find((d) => d.id === data?.nextDocument.id),
        );

        setSelectedDoc(doc);
        setIsDocumentViewed(false);
      } else {
        areAllDocsAcknowledgedByCandidate ? completeStepForUser() : handleGoBack();
      }
    }
  };

  const { mutate: acknowledgeDocument } = mutateAcknowledgeDocument(
    tenantId!,
    clientOrganizationId!,
    selectedConfigurationId!,
    userId!,
    handleAcknowledgement,
  );

  const handleGoBack = () => {
    if (selectedDoc?.candidateAcknowledgementRequired) {
      setSelectedDoc(null);
      queryClient.invalidateQueries({
        queryKey: ['getFlowAndUpdateRedux'],
      });
    } else {
      setSelectedDoc(null);
    }
  };

  if (selectedDoc) {
    return (
      <SignDocumentPreview
        acknowledgeDocument={acknowledgeDocument}
        updateDocumentViewStatus={updateDocumentViewStatus}
        selectedDoc={selectedDoc}
        selectedDocName={t(`${selectedDoc.label}`)}
        goBack={handleGoBack}
        isDocumentViewed={isDocumentViewed}
        setIsDocumentViewed={setIsDocumentViewed}
      />
    );
  }

  const isIntroVisible = hasIntro && !signed;

  if (isLoadingStepper || (isFetching && !data.length)) return <Loader />;

  if (isIntroVisible) return <OOSignDocumentStart close={() => setHasIntro(false)} />;
  return (
    <>
      {isAwaitingDocumentStatus || isCompleting || isPending || (isSuccess && !isError) ? (
        <Loader />
      ) : (
        <CandidatePageLayout hideOverview={false}>
          <Box
            m={{ md: 2 }}
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            justifyContent="space-between"
            minHeight="80vh"
          >
            <Box>
              {getStepperComponent(themeAdditionalData.stepper)}
              {(themeAdditionalData.stepper === 'none' ||
                !currentPageAndStep?.stepLabel ||
                !i18n.exists(currentPageAndStep?.stepLabel, { ns: 'stepper' })) && (
                <Box
                  display="flex"
                  justifyContent="left"
                  width="100%"
                  style={{
                    marginBottom: 40,
                    marginTop: 12,
                  }}
                >
                  <h3 className="tag-ds">{t(stepLabelKey)}</h3>
                </Box>
              )}

              <Box display="flex" justifyContent="center" width="100%">
                <Box width="100%">
                  {isDocumentsMissing && (
                    <Box className="tag-ds">
                      <Notification
                        icon={<span className="material-icons">info_outline</span>}
                        info
                        show={true}
                        className={styles.notificationWarning}
                      >
                        <NotificationBody>
                          {t('candidate_recruiter:APPROVAL.SIGNATURE.missingDocuments')}
                          <br />
                          Fetching Attempt: {retryCount <= MAX_ATTEMPTS ? retryCount : MAX_ATTEMPTS}/{MAX_ATTEMPTS}
                        </NotificationBody>
                        <NotificationFooter>
                          <Button
                            style={{ marginLeft: 'auto', width: '30%' }}
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={handleRefetchDocuments}
                          >
                            {t('candidate_recruiter:APPROVAL.SIGNATURE.refresh')}
                          </Button>
                        </NotificationFooter>
                      </Notification>
                    </Box>
                  )}
                  {documentsForSignature.map((doc: DocumentsForSignatureModel, i: number) => {
                    if (!doc) {
                      return <></>;
                    }
                    const prefix = doc?.label.split('.')[0].toLowerCase();
                    const userDocuments = [...(doc.userDocuments || [])].sort(
                      (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                    );
                    const documentToUse = userDocuments[0];
                    const isDocumentSigned = documentToUse?.additionalData?.signed === true;
                    const isDocumentAvailable = doc.generated ? !!documentToUse?.url : true;
                    const isDocumentViewed = documentToUse?.additionalData?.seen === true;
                    const isDocumentAcknowledged = documentToUse?.additionalData?.acknowledged === true;
                    const getStatus = () => {
                      if (isDocumentSigned) {
                        return SignDocumentsStatus.SIGNED;
                      }
                      if (!isDocumentAvailable) {
                        return SignDocumentsStatus.DOCUMENT_NOT_AVAILABLE;
                      }
                      if (doc.candidateAcknowledgementRequired) {
                        if (!isDocumentAcknowledged) {
                          return isDocumentViewed ? SignDocumentsStatus.SEEN : SignDocumentsStatus.NOT_SEEN;
                        }
                        return SignDocumentsStatus.ACKNOWLEDGED;
                      }
                      if (!doc.isCandidateSignatureRequired) {
                        return SignDocumentsStatus.PREVIEW;
                      }
                      if (doc.isCandidateSignatureRequired && !isDocumentSigned) {
                        return SignDocumentsStatus.SIGNATURE_MISSING;
                      }
                      return SignDocumentsStatus.MISSING;
                    };
                    const getDate = () => {
                      if (isDocumentAcknowledged) {
                        return documentToUse?.additionalData?.acknowledgedAtDate as string;
                      }
                      if (isDocumentViewed) {
                        return documentToUse?.additionalData?.seenAtDate as string;
                      }
                      return '';
                    };
                    return (
                      <SignDocumentButton
                        isMandatory={doc.isCandidateSignatureRequired || doc.candidateAcknowledgementRequired}
                        key={i}
                        dictionaries={dictionaries}
                        title={t(`${prefix}:${doc.label}`)}
                        status={getStatus()}
                        date={getDate()}
                        statusClassName={`document-button__status document-button__status--${getStatus().toLowerCase()}`}
                        disabled={isDocumentSigned || !isDocumentAvailable}
                        onClick={async () => {
                          const d = { ...doc };
                          if (!isDocumentAvailable) {
                            return;
                          }
                          if (isDocumentSigned || !doc.isCandidateSignatureRequired) {
                            setSelectedDoc(d);
                          } else {
                            if (documentToUse) {
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              d.id = documentToUse.id;
                            }
                            signDocument(d);
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
            <BottomBox py={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => completeStepForUser()}
                disabled={
                  ((isCompleting || isCompletingSuccess) && !isCompletingFailed) ||
                  !areAllDocsSignedByCandidate ||
                  !areAllDocsAcknowledgedByCandidate
                }
              >
                {t('candidate:GENERAL.PAGES.nextPage')}
              </Button>
            </BottomBox>
          </Box>
        </CandidatePageLayout>
      )}
    </>
  );
};
